import React, { useEffect, useState } from 'react';
import {
  FileCheck,
  FileX2,
  AlertCircle,
  CheckCircle2,
  Info,
  DollarSign,
  Loader2,
} from 'lucide-react';
import { FileUpload } from '../common/FileUpload';
import { PaymentComplementData, PaymentComplementValidation } from '../../types/invoice';
import { extractComplementData, validatePaymentComplement } from '../../services/complementService';
import { formatNumber } from '../../utils/formatters';
import { validateMatchingFilenames } from '../../utils/xmlParser';
import { logger } from '@/lib/logger';

interface ComplementUploadProps {
  xmlFile: File | null;
  pdfFile: File | null;
  onXmlChange: (file: File | null) => void;
  onPdfChange: (file: File | null) => void;
  invoiceUuid: string;
  invoiceIssuerRfc: string;
  onValidationChange: (
    validation: PaymentComplementValidation | null,
    data: PaymentComplementData | null
  ) => void;
}

export const ComplementUpload: React.FC<ComplementUploadProps> = ({
  xmlFile,
  pdfFile,
  onXmlChange,
  onPdfChange,
  invoiceUuid,
  invoiceIssuerRfc,
  onValidationChange,
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [validation, setValidation] = useState<PaymentComplementValidation | null>(null);
  const [complementData, setComplementData] = useState<PaymentComplementData | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  useEffect(() => {
    const validateXml = async () => {
      if (!xmlFile) {
        setValidation(null);
        setComplementData(null);
        onValidationChange(null, null);
        return;
      }

      setIsValidating(true);
      setFileError(null);

      try {
        const data = await extractComplementData(xmlFile);
        setComplementData(data);

        if (!data) {
          const failedValidation: PaymentComplementValidation = {
            isValid: false,
            errors: [
              'No se pudo extraer la información del complemento de pago.',
              'Verifica que el archivo sea un XML válido de tipo CFDI P (Pago) con TimbreFiscalDigital.',
            ],
          };
          setValidation(failedValidation);
          onValidationChange(failedValidation, null);
          return;
        }

        const validationResult = validatePaymentComplement(data, invoiceUuid, invoiceIssuerRfc);
        setValidation(validationResult);
        onValidationChange(validationResult, data);
      } catch (error) {
        logger.error('Error validating complement', { error });
        const errorValidation: PaymentComplementValidation = {
          isValid: false,
          errors: ['Error al procesar el archivo XML'],
        };
        setValidation(errorValidation);
        onValidationChange(errorValidation, null);
      } finally {
        setIsValidating(false);
      }
    };

    validateXml();
  }, [xmlFile, invoiceUuid, invoiceIssuerRfc, onValidationChange]);

  useEffect(() => {
    if (xmlFile && pdfFile) {
      const { valid, error } = validateMatchingFilenames(xmlFile, pdfFile);
      setFileError(valid ? null : error || null);
    } else {
      setFileError(null);
    }
  }, [xmlFile, pdfFile]);

  const isComplete = xmlFile && pdfFile && validation?.isValid && !fileError;

  return (
    <div className="mt-6 p-5 bg-green-50/50 rounded-2xl border border-green-200/50">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2 bg-green-100 rounded-xl">
          <DollarSign size={20} className="text-green-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-bold text-gray-900">Complemento de Pago</h4>
          <p className="text-sm text-gray-600 mt-1">
            Adjunta el CFDI tipo P que comprueba el pago parcial o diferido de esta factura.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 p-3 bg-white/80 rounded-xl border border-green-200/50 mb-4">
        <Info size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-gray-600">
          <p className="font-medium text-gray-700 mb-1">Requisitos del Complemento:</p>
          <ul className="space-y-1 list-disc list-inside text-gray-500">
            <li>
              Tipo de comprobante: <strong>P (Pago)</strong>
            </li>
            <li>
              Documento relacionado: <strong>{invoiceUuid || '(carga primero la factura)'}</strong>
            </li>
            <li>RFC emisor debe coincidir con la factura</li>
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="relative">
          <FileUpload
            label="XML del Complemento"
            accept=".xml"
            file={xmlFile}
            onChange={onXmlChange}
            iconType="xml"
          />
          {isValidating && (
            <div className="absolute inset-0 bg-white/80 rounded-2xl flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-green-500 animate-spin" />
            </div>
          )}
        </div>
        <FileUpload
          label="PDF del Complemento"
          accept=".pdf"
          file={pdfFile}
          onChange={onPdfChange}
          iconType="pdf"
        />
      </div>

      {fileError && (
        <div className="mt-4 p-3 bg-red-50 rounded-xl border border-red-200 flex items-start gap-2">
          <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{fileError}</p>
        </div>
      )}

      {validation && !isValidating && (
        <div
          className={`mt-4 p-4 rounded-xl border ${
            validation.isValid
              ? 'bg-partrunner-green/5 border-partrunner-green/20'
              : 'bg-red-50 border-red-200'
          }`}
        >
          <div className="flex items-start gap-3">
            {validation.isValid ? (
              <>
                <CheckCircle2 size={20} className="text-partrunner-green flex-shrink-0" />
                <div>
                  <p className="font-semibold text-partrunner-green">Complemento de pago válido</p>
                  {complementData && (
                    <div className="mt-2 text-sm text-gray-600 space-y-1">
                      <p>
                        <span className="text-gray-500">UUID:</span> {complementData.uuid}
                      </p>
                      {complementData.amountPaid != null && (
                        <p>
                          <span className="text-gray-500">Monto pagado:</span> $
                          {formatNumber(complementData.amountPaid)} {complementData.currency}
                        </p>
                      )}
                      {complementData.paymentDate && (
                        <p>
                          <span className="text-gray-500">Fecha de pago:</span>{' '}
                          {complementData.paymentDate}
                        </p>
                      )}
                      {complementData.partialityNumber != null && (
                        <p>
                          <span className="text-gray-500">Parcialidad:</span>{' '}
                          {complementData.partialityNumber}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <FileX2 size={20} className="text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-red-700">Errores de validación</p>
                  <ul className="mt-2 text-sm text-red-600 space-y-1">
                    {validation.errors.map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-red-400">•</span>
                        {error}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {xmlFile && pdfFile && (
        <div
          className={`mt-4 flex items-center gap-2 p-3 rounded-xl ${
            isComplete
              ? 'bg-partrunner-green/10 text-partrunner-green'
              : 'bg-amber-100 text-amber-700'
          }`}
        >
          {isComplete ? (
            <>
              <FileCheck size={18} />
              <span className="text-sm font-medium">Complemento de pago completo y validado</span>
            </>
          ) : (
            <>
              <AlertCircle size={18} />
              <span className="text-sm font-medium">Corrige los errores para continuar</span>
            </>
          )}
        </div>
      )}
    </div>
  );
};
